import React from 'react';
import { Clickable, Text, Stack } from '@moda/om';
import ExitIcon from '@moda/icons/exit-16';
import { formatPrice, priceToNumber } from '@moda/portal-stanchions';
import { useCountry } from '../CountrySelector';

import './EstimatedVat.scss';

type Price = {
  currency: string | null;
  price: string | number | null;
};

type Props = {
  currentPrice: Price;
  vatPrice: Price;
  onClose?(): void;
};

export const EstimatedVat: React.FC<Props> = ({ currentPrice, vatPrice, onClose }) => {
  const {
    country: { currencyCode, name: countryName }
  } = useCountry();

  const priceDifference = (
    priceToNumber(currentPrice.price) - priceToNumber(vatPrice.price)
  ).toString();

  return (
    <Stack space={4} className="EstimatedVat">
      <Clickable className="EstimatedVat__close" onClick={onClose}>
        <ExitIcon />
      </Clickable>
      <Text treatment="h3" family="serif">
        Estimated VAT
      </Text>

      <Stack space={2} direction="horizontal">
        <Text treatment="eyebrow" color="cement">
          Local Currency:
        </Text>
        <Text treatment="eyebrow">
          {countryName} ({currencyCode})
        </Text>
      </Stack>

      <Text treatment="body1">
        The estimated price displayed for this product is equivalent to local currency, but does not
        include any shipping fees or duties:
      </Text>

      <div className="EstimatedVat__price-breakdown">
        <Stack className="EstimatedVat__column" space={1}>
          <Text treatment="bold1">Includes VAT</Text>
          <Text>{formatPrice(currentPrice.currency, currentPrice.price)}</Text>
        </Stack>
        <Stack space={1}>
          <Text>Item Subtotal</Text>
          <Text className="EstimatedVat__align">
            {formatPrice(currentPrice.currency, priceDifference)}
          </Text>
        </Stack>
        <Stack space={1}>
          <Text>Estimated VAT</Text>
          <Text className="EstimatedVat__align">
            {formatPrice(vatPrice.currency, vatPrice.price)}
          </Text>
        </Stack>
      </div>

      <Stack className="EstimatedVat__info" space={1}>
        {currencyCode === 'GBP' && (
          <Text treatment="bold1">Shipping outside the UK? Please read:</Text>
        )}
        {currencyCode === 'EUR' && (
          <Text treatment="bold1">Shipping outside the Europe? Please read:</Text>
        )}
        <Text treatment="body1">
          Any final calculations (including any duties, taxes, and shipping costs) will be
          determined by the product’s final shipping destination submited at checkout.
        </Text>
      </Stack>

      <Stack space={1}>
        <Text treatment="bold1">Have more questions? We have answers.</Text>
        <Text treatment="body1">
          <li>
            Visit our{' '}
            <a
              className="EstimatedVat__link"
              href="https://help.modaoperandi.com/hc/en-us/articles/211521823-Taxes-Duties"
              rel="noopener noreferrer"
              target="_blank"
            >
              FAQ about Duties and Taxes
            </a>
            .
          </li>
          <li>
            Contact our{' '}
            <a className="EstimatedVat__link" href="mailto:care@modaoperandi.com">
              Care Team
            </a>
            .
          </li>
        </Text>
      </Stack>
    </Stack>
  );
};
